import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  Card,
  CardContent,
  CardActions,
} from "@mui/material";
import axios from "axios";
import Header from "components/Header";
import { DataGrid } from "@mui/x-data-grid";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";
import { Add, Delete, Edit } from "@mui/icons-material";

const PricingPlans = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [formData, setFormData] = useState({ 
    name: "", 
    price: 0, 
    billingCycle: "monthly", 
    features: [] 
  });
  const [currentFeature, setCurrentFeature] = useState("");
  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Fetch pricing plans from the API
  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${apiUrl}/pricing`);
      setPlans(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching pricing plans:", error);
      setIsLoading(false);
      setErrorMessage("Error fetching pricing plans");
      setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  // Open the form for adding or editing
  const handleOpenModal = (plan = null) => {
    setSelectedPlan(plan);
    setFormData(plan ? { 
      ...plan, 
      features: [...plan.features] 
    } : { 
      name: "", 
      price: 0, 
      billingCycle: "monthly", 
      features: [] 
    });
    setOpenModal(true);
    setErrors({});
  };

  // Close the form
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedPlan(null);
    setErrors({});
  };

  // Handle form data change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Add a feature to the list
  const handleAddFeature = () => {
    if (currentFeature.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, currentFeature.trim()]
      });
      setCurrentFeature("");
    }
  };

  // Remove a feature from the list
  const handleRemoveFeature = (index) => {
    const newFeatures = [...formData.features];
    newFeatures.splice(index, 1);
    setFormData({ ...formData, features: newFeatures });
  };

  // Form validation function
  const validateForm = () => {
    const newErrors = {};

    // Validate name
    if (!formData.name.trim()) {
      newErrors.name = "Plan name is required.";
    }

    // Validate price
    if (formData.price < 0) {
      newErrors.price = "Price must be a positive number.";
    }

    // Validate features
    if (formData.features.length === 0) {
      newErrors.features = "At least one feature is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Add or update a pricing plan
  const handleSubmit = async () => {
    if (!validateForm()) {
      return; // Stop submission if the form is invalid
    }

    try {
      if (selectedPlan) {
        // Update pricing plan
        const response = await axios.put(`${apiUrl}/pricing/${selectedPlan._id}`, formData);
        setPlans(prevData =>
          prevData.map(item => (item._id === selectedPlan._id ? response.data : item))
      )} else {
        // Create a new pricing plan
        const response = await axios.post(`${apiUrl}/pricing`, formData);
        setPlans(prevData => [...prevData, response.data]);
      }
      handleCloseModal();
    } catch (error) {
      console.error("Error saving pricing plan:", error);
      setErrorMessage("Error saving pricing plan.");
      setSnackbarOpen(true);
    }
  };

  // Delete a pricing plan
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${apiUrl}/pricing/${id}`);
      setPlans(prevData => prevData.filter(item => item._id !== id));
    } catch (error) {
      console.error("Error deleting pricing plan:", error);
      setErrorMessage("Error deleting pricing plan.");
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const columns = [
    { field: "name", headerName: "Plan Name", flex: 0.3 },
    { 
      field: "price", 
      headerName: "Price", 
      flex: 0.2,
      renderCell: (params) => (
        `$${params.row.price}/${params.row.billingCycle}`
      )
    },
    { 
      field: "features", 
      headerName: "Features", 
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {params.row.features.map((feature, index) => (
            <Chip key={index} label={feature} size="small" />
          ))}
        </Box>
      )
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.3,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleOpenModal(params.row)}
            startIcon={<Edit />}
            sx={{ mr: 1, backgroundColor: "#4d547d" }}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={() => handleDelete(params.row._id)}
            startIcon={<Delete />}
            sx={{ backgroundColor: "white", color: "#191f45" }}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box m="1.5rem 2.5rem">
      <Header title="PRICING PLANS" subtitle="Manage your pricing plans" />
      
      <Button 
        variant="contained" 
        color="primary" 
        onClick={() => handleOpenModal(null)}
        startIcon={<Add />}
        sx={{ mb: 3 }}
      >
        Add New Plan
      </Button>

      {/* Pricing Plans Cards View */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {plans.map((plan) => (
          <Grid item xs={12} md={4} key={plan._id}>
            <Card sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              border: '1px solid #4d547d'
            }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography gutterBottom variant="h5" component="h2">
                  {plan.name}
                </Typography>
                <Typography variant="h4" color="text.primary" sx={{ mb: 2 }}>
                  ${plan.price}/{plan.billingCycle}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {plan.features.map((feature, index) => (
                    <Typography key={index} variant="body2">
                      • {feature}
                    </Typography>
                  ))}
                </Box>
              </CardContent>
              <CardActions>
                <Button 
                  size="small" 
                  onClick={() => handleOpenModal(plan)}
                  sx={{ color: "#4d547d" }}
                >
                  Edit
                </Button>
                <Button 
                  size="small" 
                  onClick={() => handleDelete(plan._id)}
                  sx={{ color: "#ff4444" }}
                >
                  Delete
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Data Grid View */}
      <Box mt="40px" height="75vh">
        <DataGrid
          loading={isLoading}
          getRowId={(row) => row._id}
          rows={plans || []}
          columns={columns}
          pageSize={5}
          sx={{ backgroundColor: "#191f45", color: "white" }}
          components={{ Toolbar: DataGridCustomToolbar }}
          componentsProps={{
            toolbar: { searchInput, setSearchInput, setSearch },
          }}
        />
      </Box>

      {/* Add/Edit Plan Dialog */}
      <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="sm">
        <DialogTitle>{selectedPlan ? "Edit Pricing Plan" : "Add New Pricing Plan"}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel id="plan-name-label">Plan Name</InputLabel>
            <Select
              labelId="plan-name-label"
              id="name"
              name="name"
              value={formData.name}
              label="Plan Name"
              onChange={handleChange}
              error={!!errors.name}
            >
              <MenuItem value="Basic Plan">Basic Plan</MenuItem>
              <MenuItem value="Standard Plan">Standard Plan</MenuItem>
              <MenuItem value="Premium Plan">Premium Plan</MenuItem>
            </Select>
            {errors.name && (
              <Typography color="error" variant="body2">
                {errors.name}
              </Typography>
            )}
          </FormControl>

          <TextField
            margin="normal"
            label="Price"
            type="number"
            fullWidth
            variant="outlined"
            name="price"
            value={formData.price}
            onChange={handleChange}
            error={!!errors.price}
            helperText={errors.price}
            InputProps={{
              startAdornment: "$",
            }}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel id="billing-cycle-label">Billing Cycle</InputLabel>
            <Select
              labelId="billing-cycle-label"
              id="billingCycle"
              name="billingCycle"
              value={formData.billingCycle}
              label="Billing Cycle"
              onChange={handleChange}
            >
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
            </Select>
          </FormControl>

          <Box margin="normal">
            <Typography variant="subtitle1" gutterBottom>
              Features
            </Typography>
            {errors.features && (
              <Typography color="error" variant="body2">
                {errors.features}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                variant="outlined"
                value={currentFeature}
                onChange={(e) => setCurrentFeature(e.target.value)}
                placeholder="Add a feature"
              />
              <Button 
                variant="contained" 
                onClick={handleAddFeature}
                sx={{ backgroundColor: "#4d547d" }}
              >
                Add
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.features.map((feature, index) => (
                <Chip
                  key={index}
                  label={feature}
                  onDelete={() => handleRemoveFeature(index)}
                />
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{ backgroundColor: "#4d547d" }}
          >
            {selectedPlan ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PricingPlans;